/**
 * ClientsPage - Página de gestión de clientes (CRUD)
 */
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import {
  PageHeader,
  LoadingSpinner,
  ConfirmDialog,
  DataTable,
} from '../components/common';
import { useNotification } from '../context/NotificationContext';
import * as catalogoService from '../services/catalogoService';

// Esquema de validación
const clienteSchema = yup.object().shape({
  codigo: yup.string().required('El código es requerido'),
  nombre: yup.string().required('El nombre es requerido'),
  ruc: yup.string(),
  direccion: yup.string(),
  telefono: yup.string(),
  email: yup.string().email('Ingrese un email válido'),
  activo: yup.boolean(),
});

const ClientsPage = () => {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(clienteSchema),
    defaultValues: {
      codigo: '',
      nombre: '',
      ruc: '',
      direccion: '',
      telefono: '',
      email: '',
      activo: true,
    },
  });

  // Cargar clientes
  const loadClientes = async () => {
    try {
      setLoading(true);
      const data = await catalogoService.getClientes();
      setClientes(data);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      showError('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClientes();
  }, []);

  // Abrir diálogo para crear
  const handleOpenCreate = () => {
    setEditingCliente(null);
    reset({
      codigo: '',
      nombre: '',
      ruc: '',
      direccion: '',
      telefono: '',
      email: '',
      activo: true,
    });
    setOpenDialog(true);
  };

  // Abrir diálogo para editar
  const handleOpenEdit = (cliente) => {
    setEditingCliente(cliente);
    reset({
      codigo: cliente.codigo || '',
      nombre: cliente.nombre || '',
      ruc: cliente.ruc || '',
      direccion: cliente.direccion || '',
      telefono: cliente.telefono || '',
      email: cliente.email || '',
      activo: cliente.activo !== false,
    });
    setOpenDialog(true);
  };

  // Cerrar diálogo
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCliente(null);
    reset();
  };

  // Guardar cliente (crear o actualizar)
  const onSubmit = async (data) => {
    try {
      if (editingCliente) {
        await catalogoService.updateCliente(editingCliente.id, data);
        showSuccess('Cliente actualizado correctamente');
      } else {
        await catalogoService.createCliente(data);
        showSuccess('Cliente creado correctamente');
      }

      handleCloseDialog();
      loadClientes();
    } catch (error) {
      console.error('Error al guardar cliente:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Error al guardar cliente';
      showError(errorMessage);
    }
  };

  // Confirmar eliminación
  const handleDelete = (cliente) => {
    setDeleteConfirm(cliente);
  };

  // Eliminar cliente
  const confirmDelete = async () => {
    try {
      await catalogoService.deleteCliente(deleteConfirm.id);
      showSuccess('Cliente eliminado correctamente');
      setDeleteConfirm(null);
      loadClientes();
    } catch (error) {
      console.error('Error al eliminar cliente:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Error al eliminar cliente';
      showError(errorMessage);
    }
  };

  // Columnas de la tabla
  const columns = [
    {
      field: 'codigo',
      headerName: 'Código',
    },
    {
      field: 'nombre',
      headerName: 'Nombre',
    },
    {
      field: 'ruc',
      headerName: 'RUC/Cédula',
    },
    {
      field: 'telefono',
      headerName: 'Teléfono',
    },
    {
      field: 'email',
      headerName: 'Email',
    },
    {
      field: 'activo',
      headerName: 'Estado',
      type: 'status',
    },
  ];

  if (loading) {
    return <LoadingSpinner message="Cargando clientes..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Clientes"
        breadcrumbs={[{ label: 'Clientes' }]}
        actions={[
          {
            label: 'Nuevo Cliente',
            icon: AddIcon,
            onClick: handleOpenCreate,
            variant: 'contained',
          },
        ]}
      />

      {/* Tabla de clientes */}
      <DataTable
        columns={columns}
        data={clientes}
        onEdit={handleOpenEdit}
        onDelete={handleDelete}
        searchPlaceholder="Buscar por nombre o RUC..."
        emptyMessage="No hay clientes registrados"
      />

      {/* Diálogo de crear/editar */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editingCliente ? 'Editar Cliente' : 'Nuevo Cliente'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('codigo')}
                  label="Código"
                  fullWidth
                  error={!!errors.codigo}
                  helperText={errors.codigo?.message}
                  autoFocus
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('ruc')}
                  label="RUC/Cédula"
                  fullWidth
                  error={!!errors.ruc}
                  helperText={errors.ruc?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  {...register('nombre')}
                  label="Nombre / Razón Social"
                  fullWidth
                  error={!!errors.nombre}
                  helperText={errors.nombre?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  {...register('direccion')}
                  label="Dirección"
                  fullWidth
                  error={!!errors.direccion}
                  helperText={errors.direccion?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('telefono')}
                  label="Teléfono"
                  fullWidth
                  error={!!errors.telefono}
                  helperText={errors.telefono?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('email')}
                  label="Email"
                  type="email"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...register('activo')}
                      defaultChecked={editingCliente?.activo !== false}
                    />
                  }
                  label="Activo"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              {editingCliente ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Diálogo de confirmación de eliminación */}
      <ConfirmDialog
        open={!!deleteConfirm}
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={confirmDelete}
        title="Eliminar Cliente"
        message={`¿Está seguro de que desea eliminar el cliente "${deleteConfirm?.nombre}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
      />
    </Box>
  );
};

export default ClientsPage;
